import { publishedAt, modifiedAt, postDateByline } from "@/data/postDates";
import { ArticleFaqs } from "@/components/site/ArticleFaqs";
import { postFaqs } from "@/data/postFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { articleSchema, faqPageSchema, canonicalLinks, jsonLd, ogImage, ogUrl, breadcrumbSchema, webPageSchema, absoluteUrl } from "@/lib/seo";
import { CashRunway } from "@/components/diagrams/CashRunway";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PhotoBand } from "@/components/site/PhotoBand";
import whiteboardPlanningImg from "@/assets/photos/whiteboard-planning.webp";

const SLUG = "a-plan-nobody-tracks-is-a-document";
const TITLE = "A plan nobody tracks is a document";
const DESCRIPTION =
  "A strategy day versus an operating system: a budget behind the plan, a forecast that is updated and a monthly rhythm where someone answers for variance.";
const CATEGORY = "Planning";
const READ = "4 min read";

export const Route = createFileRoute("/insights/a-plan-nobody-tracks-is-a-document")({
  head: () => ({
    meta: [
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage(`insights-${SLUG}`, TITLE),
      ogUrl(`/insights/${SLUG}`),
    ],
    links: canonicalLinks(`/insights/${SLUG}`),
    scripts: [
      jsonLd(faqPageSchema(`/insights/${SLUG}`, postFaqs[SLUG], `${absoluteUrl(`/insights/${SLUG}`)}#article`)),
      jsonLd(breadcrumbSchema([{ name: "Insights", path: "/insights" }, { name: TITLE, path: `/insights/${SLUG}` }])),
      jsonLd(
        webPageSchema({
          path: `/insights/${SLUG}`,
          name: `${TITLE} | Ajility`,
          description: DESCRIPTION,
          dateModified: modifiedAt(SLUG),
          mainEntityId: `${absoluteUrl(`/insights/${SLUG}`)}#article`,
          image: `insights-${SLUG}`,
        }),
      ),
      jsonLd(
        articleSchema({
          title: TITLE,
          description: DESCRIPTION,
          slug: SLUG,
          datePublished: publishedAt(SLUG),
          dateModified: modifiedAt(SLUG),
          section: CATEGORY,
        }),
      ),
    ],
  }),
  component: APlanNobodyTracks,
});

function APlanNobodyTracks() {
  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative gradient-navy text-frost">
        <div className="grid-lines absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-3xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-frost/70 transition hover:text-frost"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All operating insights and case studies
          </Link>
          <span className="eyebrow on-dark mt-8 block">{CATEGORY}, {READ}</span>
          <h1 className="mt-4 font-serif text-4xl leading-[1.08] text-frost sm:text-5xl">
            {TITLE}
          </h1>
          <p className="mt-3 text-sm text-frost/70">{postDateByline(SLUG)}</p>
          <p className="mt-6 text-lg leading-relaxed text-frost/80">{DESCRIPTION}</p>
          <div className="mt-10 flex items-center gap-4 border-t border-white/10 pt-6 text-sm text-frost/70">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-frost/10 font-serif text-frost">
              A
            </div>
            <div>
              By <Link to="/about/adam-ayoub" className="font-medium text-frost underline decoration-white/30 underline-offset-4 transition hover:decoration-frost">Adam Ayoub</Link>
              <br />
              <span className="text-frost/80">Fractional COO, Ajility</span>
            </div>
          </div>
        </div>
      </section>


      <PhotoBand src={whiteboardPlanningImg} alt="A whiteboard covered in a half-erased quarterly plan with handwritten actions and owners still legible." />

      {/* Article */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <article className="article-body">
          <p>Every business Ajility walks into has a plan somewhere. A strategy day happened. Slides exist.</p>
          <p>Almost none of them have a plan with a budget behind it, a forecast that gets updated, and a monthly rhythm where someone answers for the variance.</p>
          <p>A plan nobody tracks is a document. A plan with a budget and a forecast behind it is an operating system.</p>
          <p>Earlier in my career, in-house, I owned the annual planning process for a 249-store national retailer: strategic plan, budget, forecast, and above-budget profit two years running. In a transport group, annual planning with quarterly forecasting ran alongside four consecutive years of net profit growth.</p>
          <CashRunway />
          <p>The 13-week rolling cash forecast is where most founder-led businesses should start. It's the most-asked-for finance artefact for a reason: it tells you the truth about the next quarter while you can still do something about it.</p>
          <p>
            <Link to="/specialisations/planning-budgeting-and-forecasting" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">
              See the planning, budgeting and forecasting specialisation
            </Link>
          </p>
          <ArticleFaqs faqs={postFaqs[SLUG]} />
        </article>
      </section>

      {/* CTA */}
      <section className="bg-midnight-deep">
        <div className="mx-auto max-w-4xl px-5 py-20 text-center text-frost sm:px-8">
          <h2 className="font-serif text-4xl leading-tight sm:text-5xl">
            Want to know where your real bottleneck is?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-frost/75">
            Book a 30-minute fit call, a structured conversation rather than a pitch, about
            where the constraint actually sits in your business.
          </p>
          <BookFitCall
            placement={`insights_article_${SLUG}_footer`}
            buttonClassName="mt-8 inline-flex items-center gap-2 rounded-full bg-frost px-7 py-3.5 text-sm font-medium text-midnight-deep transition hover:bg-white"
            noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
          >
            Book a fit call <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </BookFitCall>
          <p className="mt-8 font-serif italic text-frost/80">
            "Strategy without execution is hallucination."
          </p>
        </div>
      </section>
    </div>
  );
}
